import { useState, useEffect, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Check, CheckCheck, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { SEVERITY_STYLES, ALERT_STATUS_STYLES, ALERT_TYPE_LABELS, fmtWhen } from './rmmUtils';

export const AlertsTab = ({ onChanged }) => {
  const [alerts, setAlerts] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [severityFilter, setSeverityFilter] = useState('all');

  const load = useCallback(() => {
    rmmAPI.listAlerts({
      status: statusFilter === 'all' ? undefined : statusFilter,
      severity: severityFilter === 'all' ? undefined : severityFilter,
    }).then((res) => setAlerts(res.data)).catch(() => setAlerts([]));
  }, [statusFilter, severityFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await rmmAPI.actOnAlert(id, action);
      toast.success(`Alert ${action}d`);
      load(); onChanged();
    } catch { toast.error(`Failed to ${action} alert`); }
  };

  const makeTicket = async (id) => {
    try {
      const res = await rmmAPI.alertToTicket(id);
      toast.success(`Ticket ${res.data.ticket_number} created from alert`);
      load(); onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    }
  };

  return (
    <div className="space-y-4" data-testid="rmm-alerts-tab">
      <div className="flex gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-slate-200" data-testid="rmm-alert-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active (open + ack)</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="acknowledged">Acknowledged</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-36 bg-white border-slate-200" data-testid="rmm-alert-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="warning">Warning</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-4 space-y-2" data-testid="rmm-alerts-list">
          {!alerts ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : alerts.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No alerts match this filter.</p>
          ) : alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between p-3 rounded-xl border border-slate-200 gap-3 flex-wrap" data-testid={`rmm-alert-${a.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={SEVERITY_STYLES[a.severity] || ''}>{a.severity}</Badge>
                  <span className="text-sm font-medium text-slate-800">{ALERT_TYPE_LABELS[a.type] || a.type}</span>
                  <Badge className={ALERT_STATUS_STYLES[a.status] || ''}>{a.status}</Badge>
                  {a.ticket_number && <Badge variant="outline" className="text-cyan-600 border-cyan-200" data-testid={`rmm-alert-ticket-${a.id}`}><Ticket className="w-3 h-3 mr-1" />{a.ticket_number}</Badge>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {a.message} · {a.customer_name || '—'} · {fmtWhen(a.created_at)}
                  {a.acknowledged_by ? ` · ack by ${a.acknowledged_by}` : ''}
                  {a.resolved_by ? ` · resolved by ${a.resolved_by}` : ''}
                </p>
              </div>
              {(a.status === 'open' || a.status === 'acknowledged') && (
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {!a.ticket_id && (
                    <Button size="sm" variant="outline" className="rounded-full text-cyan-600 border-cyan-200 hover:bg-cyan-50" onClick={() => makeTicket(a.id)} data-testid={`rmm-alert-maketicket-${a.id}`}>
                      <Ticket className="w-4 h-4 mr-1" /> Create Ticket
                    </Button>
                  )}
                  {a.status === 'open' && (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(a.id, 'acknowledge')} data-testid={`rmm-ack-${a.id}`}>
                      <Check className="w-4 h-4 mr-1" /> Acknowledge
                    </Button>
                  )}
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 rounded-full" onClick={() => act(a.id, 'resolve')} data-testid={`rmm-resolve-${a.id}`}>
                    <CheckCheck className="w-4 h-4 mr-1" /> Resolve
                  </Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
